import type { ClassReference, SourceTarget } from "./types.js";

export function normalizeRelativePath(value: string): string {
  const segments = value
    .replaceAll("\\", "/")
    .split("/")
    .filter(
      (segment) =>
        segment.length > 0 && segment !== "." && segment !== "..",
    );
  return segments.join("/") || value;
}

export function classSourceRelativePath(reference: ClassReference): string {
  const path = normalizeRelativePath(reference.entryPath);
  return /\.class$/iu.test(path)
    ? path.replace(/\.class$/iu, ".java")
    : `${path}.java`;
}

export function appendRelativePath(base: string, segment: string): string {
  const normalizedBase = normalizeRelativePath(base).replace(/\/+$/u, "");
  const normalizedSegment = segment
    .replaceAll("\\", "/")
    .split("/")
    .filter((part) => part.length > 0 && part !== "." && part !== "..")
    .join("/");
  return normalizedSegment.length === 0
    ? normalizedBase
    : `${normalizedBase}/${normalizedSegment}`;
}

function classSegments(internalName: string): readonly string[] {
  const slash = internalName.lastIndexOf("/");
  return internalName
    .slice(slash + 1)
    .split("$")
    .filter((segment) => segment.length > 0);
}

export function sourceTargetRelativePath(
  reference: ClassReference,
  target: SourceTarget,
): string {
  let path = classSourceRelativePath(reference);
  const segments = classSegments(target.internalName);
  for (const segment of segments) {
    path = appendRelativePath(path, segment);
  }

  switch (target.kind) {
    case "class":
      return path;
    case "field":
      return appendRelativePath(path, target.name);
    case "constructor":
      return appendRelativePath(
        path,
        `${segments.at(-1) ?? reference.displayName}${target.descriptor}`,
      );
    case "method":
      return appendRelativePath(path, `${target.name}${target.descriptor}`);
  }
}
